import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';

// Inline SVG flags — small, sharp, no external dependency
const Flag = {
  fr: () => (
    <svg viewBox="0 0 3 2" className="w-5 h-3.5 rounded-sm overflow-hidden shrink-0" aria-hidden="true">
      <rect width="1" height="2" x="0" fill="#0055A4" />
      <rect width="1" height="2" x="1" fill="#FFFFFF" />
      <rect width="1" height="2" x="2" fill="#EF4135" />
    </svg>
  ),
  en: () => (
    <svg viewBox="0 0 60 30" className="w-5 h-3.5 rounded-sm overflow-hidden shrink-0" aria-hidden="true">
      <clipPath id="t"><path d="M0,0v30h60V0z" /></clipPath>
      <path d="M0,0v30h60V0z" fill="#012169" />
      <path d="M0,0 60,30 M60,0 0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 60,30 M60,0 0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0v30 M0,15h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0v30 M0,15h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  ),
  de: () => (
    <svg viewBox="0 0 5 3" className="w-5 h-3.5 rounded-sm overflow-hidden shrink-0" aria-hidden="true">
      <rect width="5" height="1" y="0" fill="#000" />
      <rect width="5" height="1" y="1" fill="#DD0000" />
      <rect width="5" height="1" y="2" fill="#FFCE00" />
    </svg>
  ),
  lb: () => (
    <svg viewBox="0 0 5 3" className="w-5 h-3.5 rounded-sm overflow-hidden shrink-0" aria-hidden="true">
      <rect width="5" height="1" y="0" fill="#ED2939" />
      <rect width="5" height="1" y="1" fill="#FFFFFF" />
      <rect width="5" height="1" y="2" fill="#00A1DE" />
    </svg>
  )
};

const LANGUAGES = [
  { code: 'fr', label: 'Français',        short: 'FR', flag: 'fr' },
  { code: 'en', label: 'English',         short: 'EN', flag: 'en' },
  { code: 'de', label: 'Deutsch',         short: 'DE', flag: 'de' },
  { code: 'lb', label: 'Lëtzebuergesch',  short: 'LU', flag: 'lb' }
];

const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  const CurrentFlag = Flag[current.flag];

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={t('lang.label')}
        title={t('lang.label')}
        className="inline-flex items-center gap-1.5 px-2.5 h-9 rounded-md border border-slate-200 hover:border-ter-blue hover:bg-slate-50 text-slate-700 transition-colors"
      >
        <Globe className="w-3.5 h-3.5 text-slate-500" />
        <CurrentFlag />
        <span className="text-xs font-bold tracking-wide hidden sm:inline">{current.short}</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-60 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden z-50 slide-in">
          <div className="px-3 py-2 border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            {t('lang.label')}
          </div>
          <ul className="py-1">
            {LANGUAGES.map((l) => {
              const F = Flag[l.flag];
              const active = l.code === current.code;
              return (
                <li key={l.code}>
                  <button
                    onClick={() => change(l.code)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${active ? 'bg-slate-50 font-semibold text-ter-blue' : 'hover:bg-slate-50 text-slate-700'}`}
                  >
                    <F />
                    <span className="flex-1">{l.label}</span>
                    {active && <Check className="w-4 h-4 text-ter-blue" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
